// SPA mirror of the typed conversation-note document (canonical TS source: MVP lib/memory/note-doc.ts)
// plus the deterministic inverse parser (MVP lib/memory/parse-compacted-text.ts) ported verbatim.
// deriveNoteDoc is the narrow waist the viewer renders through: prefer the route's stored/derived
// note_doc, fall back to parsing compacted_text locally (covers an un-upgraded backend or drift).

export type NoteStage = "idea" | "in_progress" | "review" | "done" | "blocked";

export interface KeyRef { kind: string; ref: string }
export type PersonRelation = "author" | "participant" | "mentioned";
export interface NotePerson { name: string; relation: PersonRelation }

export interface NoteTable { caption: string; columns: string[]; rows: string[][] }
export interface NoteChart {
  caption: string;
  kind: "bar" | "line";
  x_labels: string[];
  series: { name: string; values: number[] }[];
}
export interface NoteActionItem { text: string; owner?: string | null; due?: string | null }

// A block is the atom the viewer renders: bullets, and/or one typed figure. Subheadings are part of
// the contract even though PR1's builder emits flat blocks — never assume a block has bullets.
// The builder dual-emits action items as both `bullets` (texts) and `action_items` (structured, with
// owner/due). The renderer prefers `action_items` and skips bullets when it is present.
export interface NoteBlock {
  subheading?: string | null;
  bullets?: string[];
  code?: { lang?: string | null; text: string } | null;
  table?: NoteTable | null;
  chart?: NoteChart | null;
  action_items?: NoteActionItem[] | null;
}

export type NoteSectionKind =
  | "key_details" | "decisions" | "findings" | "code"
  | "preferences" | "open_threads" | "participants" | "action_items" | "discussion";

export interface NoteSection { kind: NoteSectionKind; blocks: NoteBlock[] }

export interface NoteOverview {
  highlights: string[];
  where_it_stands: string | null;
  stage: NoteStage | null;
  why_it_matters: string | null;
  key_refs: KeyRef[];
  people: NotePerson[];
}

export interface NoteDoc {
  v: number;
  overview: NoteOverview;
  sections: NoteSection[];
}

export const SECTION_LABELS: Record<string, string> = {
  key_details: 'Key details', decisions: 'Decisions & reasoning', findings: 'Findings & analysis',
  code: 'Code & implementation', preferences: 'Preferences & corrections', open_threads: 'Open threads',
  participants: 'Participants', action_items: 'Action items', discussion: 'Discussion',
};

export function sectionLabel(kind: string): string {
  const known = SECTION_LABELS[kind];
  if (known) return known;
  const words = kind.replace(/_/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Details';
}

// Header text (lowercased, trailing colon stripped) → section kind. Covers every header the MVP's
// renderCompactedText and renderSegment emit. Topic/One-line summary are columns, not sections.
const HEADER_TO_KIND: Record<string, NoteSectionKind | "why" | "skip"> = {
  "topic": "skip",
  "one-line summary": "skip",
  "context": "why",
  "key details": "key_details",
  "decisions & reasoning": "decisions",
  "findings & analysis": "findings",
  "code & implementation": "code",
  "preferences & corrections": "preferences",
  "open threads": "open_threads",
  "participants": "participants",
  "action items": "action_items",
  "open questions": "open_threads",
  "discussion": "discussion",
};

const HEADER_RE = /^\*{1,2}([^*]+?)\*{1,2}:?\s*(.*)$/;

export function parseCompactedTextToDoc(text: string | null | undefined): NoteDoc {
  const empty: NoteDoc = {
    v: 1,
    overview: { highlights: [], where_it_stands: null, stage: null, why_it_matters: null, key_refs: [], people: [] },
    sections: [],
  };
  if (!text || !text.trim()) return empty;

  const sections: NoteSection[] = [];
  let why: string | null = null;
  let current: { kind: NoteSectionKind | "why" | "skip"; bullets: string[]; inline: string } | null = null;

  const flush = () => {
    if (!current) return;
    if (current.kind === "why") {
      const v = [current.inline, ...current.bullets].filter(Boolean).join(" ").trim();
      if (v) why = v;
    } else if (current.kind !== "skip") {
      const bullets = [current.inline, ...current.bullets].map((b) => b.trim()).filter(Boolean);
      if (bullets.length) sections.push({ kind: current.kind, blocks: [{ bullets }] });
    }
    current = null;
  };

  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(HEADER_RE);
    if (m) {
      flush();
      const key = m[1].trim().toLowerCase().replace(/:$/, "");
      current = { kind: HEADER_TO_KIND[key] ?? "key_details", bullets: [], inline: m[2].trim() };
      continue;
    }
    const cleaned = line.replace(/^[-–•]\s*/, "");
    if (!cleaned) continue;
    if (current) current.bullets.push(cleaned);
    else current = { kind: "key_details", bullets: [cleaned], inline: "" }; // headerless leading prose
  }
  flush();

  return { ...empty, overview: { ...empty.overview, why_it_matters: why }, sections };
}

// Structural check only — the render layer is tolerant, so a cheap shape gate beats a Zod parse here
// (the contracts schema exists for consumers that want strictness; the viewer renders best-effort).
function isNoteDoc(x: unknown): x is NoteDoc {
  if (!x || typeof x !== 'object') return false;
  const d = x as Record<string, unknown>;
  return typeof d.v === 'number' && !!d.overview && typeof d.overview === 'object' && Array.isArray(d.sections);
}

export function deriveNoteDoc(input: { note_doc?: unknown; compacted_text?: string | null }): NoteDoc {
  if (isNoteDoc(input.note_doc)) return input.note_doc;
  return parseCompactedTextToDoc(input.compacted_text ?? '');
}
